/**
 * LevelData.ts - FF1-accurate level progression and stat growth
 * Contains experience tables, stat growth rates, spell learning, and class upgrade requirements
 */

// ============================================================================
// Character Class Enum (matching contract)
// ============================================================================

export enum FF1Class {
  Fighter = 0,
  Thief = 1,
  Monk = 2,
  RedMage = 3,
  WhiteMage = 4,
  BlackMage = 5,
}

// ============================================================================
// Experience Table (Levels 1-50)
// ============================================================================

/**
 * Experience required to reach each level (FF1 formula)
 * Formula: (level^3 + level) / 2
 */
export const EXPERIENCE_TABLE: number[] = [
  0,       // Level 1
  50,      // Level 2
  136,     // Level 3
  270,     // Level 4
  500,     // Level 5
  900,     // Level 6
  1500,    // Level 7
  2400,    // Level 8
  3600,    // Level 9
  5200,    // Level 10
  7300,    // Level 11
  10000,   // Level 12
  13500,   // Level 13
  18000,   // Level 14
  23600,   // Level 15
  30500,   // Level 16
  38800,   // Level 17
  48700,   // Level 18
  60400,   // Level 19
  74000,   // Level 20
  89700,   // Level 21
  107600,  // Level 22
  127900,  // Level 23
  150700,  // Level 24
  176200,  // Level 25
  204500,  // Level 26
  235800,  // Level 27
  270200,  // Level 28
  307900,  // Level 29
  349000,  // Level 30
  393700,  // Level 31
  442100,  // Level 32
  494400,  // Level 33
  550800,  // Level 34
  611500,  // Level 35
  676700,  // Level 36
  746600,  // Level 37
  821400,  // Level 38
  901300,  // Level 39
  986500,  // Level 40
  1077200, // Level 41
  1173600, // Level 42
  1275900, // Level 43
  1384300, // Level 44
  1499000, // Level 45
  1620200, // Level 46
  1748100, // Level 47
  1883000, // Level 48
  2025100, // Level 49
  2174600, // Level 50
];

// ============================================================================
// Base Stats by Class
// ============================================================================

export interface BaseStats {
  strength: number;
  agility: number;
  intelligence: number;
  vitality: number;
  luck: number;
  hp: number;
  mp: number;
}

export const BASE_STATS: Record<FF1Class, BaseStats> = {
  [FF1Class.Fighter]: {
    strength: 20,
    agility: 10,
    intelligence: 1,
    vitality: 15,
    luck: 5,
    hp: 35,
    mp: 0,
  },
  [FF1Class.Thief]: {
    strength: 10,
    agility: 20,
    intelligence: 5,
    vitality: 5,
    luck: 15,
    hp: 30,
    mp: 0,
  },
  [FF1Class.Monk]: {
    strength: 15,
    agility: 10,
    intelligence: 5,
    vitality: 20,
    luck: 5,
    hp: 33,
    mp: 0,
  },
  [FF1Class.RedMage]: {
    strength: 12,
    agility: 12,
    intelligence: 12,
    vitality: 10,
    luck: 10,
    hp: 28,
    mp: 10,
  },
  [FF1Class.WhiteMage]: {
    strength: 5,
    agility: 10,
    intelligence: 18,
    vitality: 8,
    luck: 15,
    hp: 24,
    mp: 15,
  },
  [FF1Class.BlackMage]: {
    strength: 5,
    agility: 10,
    intelligence: 20,
    vitality: 5,
    luck: 10,
    hp: 20,
    mp: 15,
  },
};

// ============================================================================
// Stat Growth Per Level
// ============================================================================

export interface StatGrowth {
  hpPerLevel: number;
  mpPerLevel: number;
  strGrowth: number;  // +1 every N levels
  agiGrowth: number;
  intGrowth: number;
  vitGrowth: number;
  luckGrowth: number;
}

export const STAT_GROWTH: Record<FF1Class, StatGrowth> = {
  [FF1Class.Fighter]: {
    hpPerLevel: 30,
    mpPerLevel: 0,
    strGrowth: 4,   // +1 STR every 4 levels
    agiGrowth: 8,
    intGrowth: 0,   // No INT growth
    vitGrowth: 4,
    luckGrowth: 10,
  },
  [FF1Class.Thief]: {
    hpPerLevel: 20,
    mpPerLevel: 0,
    strGrowth: 6,
    agiGrowth: 3,   // Fast AGI growth
    intGrowth: 10,
    vitGrowth: 10,
    luckGrowth: 4,  // Fast LUCK growth
  },
  [FF1Class.Monk]: {
    hpPerLevel: 35,  // Highest HP growth
    mpPerLevel: 0,
    strGrowth: 5,
    agiGrowth: 8,
    intGrowth: 12,
    vitGrowth: 3,    // Fast VIT growth
    luckGrowth: 10,
  },
  [FF1Class.RedMage]: {
    hpPerLevel: 22,
    mpPerLevel: 2,
    strGrowth: 6,
    agiGrowth: 6,
    intGrowth: 6,
    vitGrowth: 8,
    luckGrowth: 8,
  },
  [FF1Class.WhiteMage]: {
    hpPerLevel: 18,
    mpPerLevel: 3,
    strGrowth: 12,
    agiGrowth: 8,
    intGrowth: 4,   // Fast INT growth
    vitGrowth: 10,
    luckGrowth: 6,
  },
  [FF1Class.BlackMage]: {
    hpPerLevel: 16,  // Lowest HP growth
    mpPerLevel: 3,   // High MP growth
    strGrowth: 12,
    agiGrowth: 8,
    intGrowth: 3,    // Fastest INT growth
    vitGrowth: 12,
    luckGrowth: 8,
  },
};

// ============================================================================
// Spell Charges by Class and Level
// ============================================================================

/**
 * Max spell charges per spell level (1-8)
 * These increase as character level increases
 */
export const MAX_SPELL_CHARGES: Record<FF1Class, number[]> = {
  [FF1Class.Fighter]: [0, 0, 0, 0, 0, 0, 0, 0],
  [FF1Class.Thief]: [0, 0, 0, 0, 0, 0, 0, 0],
  [FF1Class.Monk]: [0, 0, 0, 0, 0, 0, 0, 0],
  [FF1Class.RedMage]: [3, 3, 2, 2, 2, 1, 0, 0],
  [FF1Class.WhiteMage]: [4, 4, 4, 3, 3, 2, 2, 1],
  [FF1Class.BlackMage]: [4, 4, 4, 3, 3, 2, 2, 1],
};

// ============================================================================
// Spell Learning by Class and Level
// ============================================================================

export interface SpellLearned {
  spellId: number;
  spellName: string;
  spellLevel: number; // 1-8
}

/**
 * Spells learned at each level for each class
 * Key: level number, Value: array of spells learned
 */
export const SPELLS_BY_LEVEL: Record<FF1Class, Record<number, SpellLearned[]>> = {
  [FF1Class.Fighter]: {}, // No spells until class change
  [FF1Class.Thief]: {},   // No spells until class change
  [FF1Class.Monk]: {},    // No spells

  [FF1Class.RedMage]: {
    1: [
      { spellId: 1, spellName: 'CURE', spellLevel: 1 },
      { spellId: 16, spellName: 'FIRE', spellLevel: 1 },
    ],
    3: [
      { spellId: 2, spellName: 'HARM', spellLevel: 1 },
      { spellId: 17, spellName: 'SLEEP', spellLevel: 1 },
    ],
    5: [
      { spellId: 5, spellName: 'LAMP', spellLevel: 1 },
    ],
    7: [
      { spellId: 9, spellName: 'CURE2', spellLevel: 2 },
      { spellId: 24, spellName: 'FIR2', spellLevel: 2 },
    ],
    9: [
      { spellId: 11, spellName: 'AFIR', spellLevel: 2 },
    ],
    11: [
      { spellId: 10, spellName: 'HARM2', spellLevel: 2 },
    ],
    13: [
      { spellId: 17, spellName: 'CURE3', spellLevel: 3 },
    ],
  },

  [FF1Class.WhiteMage]: {
    1: [
      { spellId: 1, spellName: 'CURE', spellLevel: 1 },
      { spellId: 2, spellName: 'HARM', spellLevel: 1 },
      { spellId: 3, spellName: 'FOG', spellLevel: 1 },
      { spellId: 4, spellName: 'RUSE', spellLevel: 1 },
    ],
    3: [
      { spellId: 9, spellName: 'CURE2', spellLevel: 2 },
      { spellId: 10, spellName: 'HARM2', spellLevel: 2 },
    ],
    5: [
      { spellId: 11, spellName: 'AFIR', spellLevel: 2 },
      { spellId: 12, spellName: 'HEAL', spellLevel: 2 },
    ],
    7: [
      { spellId: 17, spellName: 'CURE3', spellLevel: 3 },
      { spellId: 18, spellName: 'LIFE', spellLevel: 3 },
    ],
    9: [
      { spellId: 19, spellName: 'HARM3', spellLevel: 3 },
      { spellId: 20, spellName: 'HEAL2', spellLevel: 3 },
    ],
    11: [
      { spellId: 25, spellName: 'CURE4', spellLevel: 4 },
      { spellId: 26, spellName: 'HARM4', spellLevel: 4 },
    ],
    13: [
      { spellId: 27, spellName: 'LIFE2', spellLevel: 4 },
    ],
  },

  [FF1Class.BlackMage]: {
    1: [
      { spellId: 16, spellName: 'FIRE', spellLevel: 1 },
      { spellId: 17, spellName: 'SLEEP', spellLevel: 1 },
      { spellId: 18, spellName: 'LOCK', spellLevel: 1 },
      { spellId: 19, spellName: 'LIT', spellLevel: 1 },
    ],
    3: [
      { spellId: 24, spellName: 'FIR2', spellLevel: 2 },
      { spellId: 25, spellName: 'HOLD', spellLevel: 2 },
    ],
    5: [
      { spellId: 26, spellName: 'LIT2', spellLevel: 2 },
      { spellId: 27, spellName: 'SLEEP2', spellLevel: 2 },
    ],
    7: [
      { spellId: 32, spellName: 'FIR3', spellLevel: 3 },
      { spellId: 33, spellName: 'BANE', spellLevel: 3 },
    ],
    9: [
      { spellId: 34, spellName: 'LIT3', spellLevel: 3 },
      { spellId: 35, spellName: 'HOLD2', spellLevel: 3 },
    ],
    11: [
      { spellId: 40, spellName: 'FAST', spellLevel: 4 },
      { spellId: 41, spellName: 'FIRE3', spellLevel: 4 },
    ],
    13: [
      { spellId: 42, spellName: 'CONF', spellLevel: 4 },
    ],
  },
};

// ============================================================================
// Class Upgrade Requirements
// ============================================================================

export interface ClassUpgrade {
  fromClass: FF1Class;
  toClassName: string;
  requiredLevel: number;
  requiredItem?: string;
  statsBonus: {
    strength?: number;
    agility?: number;
    intelligence?: number;
    vitality?: number;
    luck?: number;
  };
  gainsSpellAccess?: boolean; // Knights gain white magic, Ninjas gain black magic
}

export const CLASS_UPGRADES: ClassUpgrade[] = [
  {
    fromClass: FF1Class.Fighter,
    toClassName: 'Knight',
    requiredLevel: 20,
    statsBonus: {
      strength: 5,
      vitality: 5,
    },
    gainsSpellAccess: true, // Gains White Magic levels 1-3
  },
  {
    fromClass: FF1Class.Thief,
    toClassName: 'Ninja',
    requiredLevel: 20,
    statsBonus: {
      agility: 5,
      luck: 5,
    },
    gainsSpellAccess: true, // Gains Black Magic levels 1-4
  },
  {
    fromClass: FF1Class.Monk,
    toClassName: 'Master',
    requiredLevel: 20,
    statsBonus: {
      strength: 5,
      vitality: 5,
      agility: 5,
    },
  },
  {
    fromClass: FF1Class.RedMage,
    toClassName: 'Red Wizard',
    requiredLevel: 20,
    statsBonus: {
      strength: 3,
      agility: 3,
      intelligence: 3,
    },
  },
  {
    fromClass: FF1Class.WhiteMage,
    toClassName: 'White Wizard',
    requiredLevel: 20,
    statsBonus: {
      intelligence: 5,
      vitality: 3,
    },
  },
  {
    fromClass: FF1Class.BlackMage,
    toClassName: 'Black Wizard',
    requiredLevel: 20,
    statsBonus: {
      intelligence: 5,
      agility: 3,
    },
  },
];

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get experience required for a specific level
 */
export function getExpForLevel(level: number): number {
  if (level < 1) return 0;
  if (level > 50) return EXPERIENCE_TABLE[50] || 0;
  return EXPERIENCE_TABLE[level - 1] || 0;
}

/**
 * Calculate how many levels a character can gain from current exp
 */
export function calculateLevelUps(currentLevel: number, currentExp: number): number {
  let levelsGained = 0;
  let level = currentLevel;

  while (level < 50 && currentExp >= getExpForLevel(level + 1)) {
    level++;
    levelsGained++;
  }

  return levelsGained;
}

/**
 * Get stats for a character at a specific level
 */
export function getStatsForLevel(
  classType: FF1Class,
  level: number
): {
  hp: number;
  mp: number;
  strength: number;
  agility: number;
  intelligence: number;
  vitality: number;
  luck: number;
} {
  const base = BASE_STATS[classType];
  const growth = STAT_GROWTH[classType];

  const levelsGained = level - 1;

  return {
    hp: base.hp + (growth.hpPerLevel * levelsGained),
    mp: base.mp + (growth.mpPerLevel * levelsGained),
    strength: base.strength + Math.floor(levelsGained / growth.strGrowth),
    agility: base.agility + Math.floor(levelsGained / growth.agiGrowth),
    intelligence: base.intelligence + (growth.intGrowth > 0 ? Math.floor(levelsGained / growth.intGrowth) : 0),
    vitality: base.vitality + Math.floor(levelsGained / growth.vitGrowth),
    luck: base.luck + Math.floor(levelsGained / growth.luckGrowth),
  };
}

/**
 * Get stat increases for leveling up from one level to the next
 */
export function getStatIncreases(
  classType: FF1Class,
  fromLevel: number,
  toLevel: number
): {
  hp: number;
  mp: number;
  strength: number;
  agility: number;
  intelligence: number;
  vitality: number;
  luck: number;
} {
  const oldStats = getStatsForLevel(classType, fromLevel);
  const newStats = getStatsForLevel(classType, toLevel);

  return {
    hp: newStats.hp - oldStats.hp,
    mp: newStats.mp - oldStats.mp,
    strength: newStats.strength - oldStats.strength,
    agility: newStats.agility - oldStats.agility,
    intelligence: newStats.intelligence - oldStats.intelligence,
    vitality: newStats.vitality - oldStats.vitality,
    luck: newStats.luck - oldStats.luck,
  };
}

/**
 * Get spells learned at a specific level
 */
export function getSpellsLearnedAtLevel(
  classType: FF1Class,
  level: number
): SpellLearned[] {
  const spells = SPELLS_BY_LEVEL[classType];
  return spells[level] || [];
}

/**
 * Check if character can upgrade class
 */
export function canUpgradeClass(classType: FF1Class, level: number): ClassUpgrade | null {
  const upgrade = CLASS_UPGRADES.find(u => u.fromClass === classType);

  if (upgrade && level >= upgrade.requiredLevel) {
    return upgrade;
  }

  return null;
}

/**
 * Get experience progress to next level (0-1)
 */
export function getExpProgress(currentLevel: number, currentExp: number): number {
  if (currentLevel >= 50) return 1.0;

  const currentLevelExp = getExpForLevel(currentLevel);
  const nextLevelExp = getExpForLevel(currentLevel + 1);
  const expInLevel = currentExp - currentLevelExp;
  const expNeeded = nextLevelExp - currentLevelExp;

  return Math.max(0, Math.min(1, expInLevel / expNeeded));
}

/**
 * Get experience needed for next level
 */
export function getExpNeededForNextLevel(currentLevel: number, currentExp: number): number {
  if (currentLevel >= 50) return 0;

  const nextLevelExp = getExpForLevel(currentLevel + 1);
  return Math.max(0, nextLevelExp - currentExp);
}
